import { File as FileType } from '@/types/core/File';
import FormElement from '@components/presentational/Form/FormElement';
import InputFile from '@components/presentational/InputFile';
import { Field, FieldProps, getIn } from 'formik';
import { useCallback, useMemo, useReducer, useState } from 'react';

const UPLOAD_PROGRESS = 'UPLOAD_PROGRESS';
const UPLOAD_END = 'UPLOAD_END';

function uploadReducer(state, action) {
  switch (action.type) {
    case UPLOAD_PROGRESS: {
      return {
        fileUploadProgress: action.progress,
        fileUploadProgressStart: state.fileUploadProgress,
      };
    }
    case UPLOAD_END: {
      return {
        fileUploadProgress: 0,
        fileUploadProgressStart: 0,
      };
    }
    default:
      return state;
  }
}

interface FileInputFormProps {
  name: string;
  label?: string;
  required?: boolean;
  allowedExtensions: string[];
  allowedMaxSize: number;
  requiredMessage?: string;
  isDisabled?: boolean;
}
export function FileInputForm({
  name,
  label,
  required = false,
  allowedExtensions,
  allowedMaxSize,
  requiredMessage,
  isDisabled = false,
}: FileInputFormProps) {
  const validateForm = useCallback(
    (value: FileType) => {
      if (required && !value.originalFilename) {
        return [requiredMessage || 'Le fichier est obligatoire'];
      }
      return undefined;
    },
    [required, requiredMessage]
  );

  const [state, dispatch] = useReducer(uploadReducer, {
    fileUploadProgress: 0,
    fileUploadProgressStart: 0,
  });

  const [request, setRequest] = useState(null);
  const [invalidFileExtension, setInvalidFileExtensions] = useState(null);
  const [invalidFileSize, setInvalidFileSize] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const acceptedExtensions = useMemo(() => {
    if (!allowedExtensions) {
      return undefined;
    }
    return allowedExtensions.map(item => `.${item}`).join(',');
  }, [allowedExtensions]);
  const onChangeFile = (field: FieldProps['field'], file: File) => {
    if (!file) return;
    const splittedFileName = file.name.split('.');
    setInvalidFileExtensions(null);
    setInvalidFileSize(null);
    setUploadError(null);

    if (
      allowedExtensions &&
      (splittedFileName.length < 2 ||
        allowedExtensions.indexOf(
          splittedFileName[splittedFileName.length - 1].toLowerCase()
        ) === -1)
    ) {
      field.onBlur(name)(true);
      setInvalidFileExtensions(file.name);
      return;
    }
    if (allowedMaxSize && file.size / 1000 / 1000 > allowedMaxSize) {
      field.onBlur(name)(true);
      setInvalidFileSize(Math.ceil(file.size / 1000 / 1000));
      return;
    }

    field.onChange(name)({
      uploadId: null,
      originalFilename: file.name,
      uploading: true,
      uploaded: false,
    });

    handleUploadFile(field, file);
  };

  const handleUploadFile = (field: FieldProps['field'], file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const request = new XMLHttpRequest();
    setRequest(request);
    request.open('POST', `/api/uploads`);
    if (request.upload) {
      request.upload.onprogress = evt => {
        dispatch({
          type: UPLOAD_PROGRESS,
          progress: Math.floor((evt.loaded / evt.total) * 100),
        });
      };

      request.onload = evt => {
        dispatch(UPLOAD_END);
        if (request.status !== 200) {
          if (request.getResponseHeader('Content-Type') === 'json+exception') {
            let errorData = JSON.parse(request.responseText);
            if (
              errorData.exception.className ===
              'NotAllowedUploadFileExtensionException'
            ) {
              setUploadError(
                `Le fichier envoyé est de type ${errorData.exception.additionalData.guessedExtension}. Ce type de fichier n'est pas autorisé`
              );
              setInvalidFileExtensions(null);
              setInvalidFileSize(null);
            }
            if (errorData.exception.className === 'FailedFileUploadException') {
              setUploadError(`L'envoi du fichier a échoué.`);
              setInvalidFileExtensions(null);
              setInvalidFileSize(null);
            }
          }

          setRequest(null);
          return field.onChange(name)({
            uploadId: null,
            originalFilename: null,
            uploading: false,
            uploaded: false,
          });
        }

        const data = JSON.parse(request.responseText);

        setRequest(null);
        field.onChange(name)({
          fileUrl: `/api/uploads/${data.data.uploadId}`,
          uploadId: data.data.uploadId,
          originalFilename: file.name,
          uploading: false,
          uploaded: true,
        });
      };

      request.send(formData);

      field.onBlur(name)(true);
    }
  };

  const handleErrors = (
    errors: string[],
    invalidFileExtension: boolean,
    invalidFileSize: boolean,
    uploadError: string
  ): Element[] => {
    let errorsComponents = [];
    if (errors) {
      errorsComponents = errors.map(errorItem => (
        <li key={errorItem}>{errorItem}</li>
      ));
    }
    if (invalidFileExtension) {
      errorsComponents.push(
        <li key="invalid-file-extension">
          Le fichier {invalidFileExtension} n'a pas une extension autorisée
        </li>
      );
    }

    if (invalidFileSize) {
      errorsComponents.push(
        <li key="invalid-file-size">
          La taille maximum de fichier est <b>{allowedMaxSize} Mo</b>, le
          fichier donné fait {invalidFileSize} Mo
        </li>
      );
    }
    if (uploadError) {
      errorsComponents.push(<li key="invalid-file-size">{uploadError}</li>);
    }

    return errorsComponents;
  };

  return (
    <Field name={name} validate={validateForm}>
      {({ field, form }: FieldProps) => {
        const errors = getIn(form.errors, name);
        const touched = getIn(form.touched, name);
        const hasError =
          (errors || invalidFileExtension || invalidFileSize || uploadError) &&
          touched;

        const errorsComponents: Element[] = handleErrors(
          errors,
          invalidFileExtension,
          invalidFileSize,
          uploadError
        );

        return (
          <>
            <FormElement
              label={label}
              isInvalid={hasError}
              errorMessage={errorsComponents}
              isRequired={required}
            >
              <InputFile
                label="Télécharger"
                inputSize="sm"
                id={name}
                onFileDelete={() => {
                  field.onChange(name)({
                    uploadId: null,
                    originalFilename: null,
                    uploading: false,
                    uploaded: false,
                  });
                  field.onBlur(name)(true);
                }}
                fileUrl={
                  field.value?.originalFilename ? field.value?.fileUrl : null
                }
                fileName={field.value?.originalFilename}
                onChange={evt => onChangeFile(field, evt.target.files[0])}
                acceptedFormats={acceptedExtensions}
                isInvalid={hasError}
                isDisabled={isDisabled}
                isRequired={required}
              />
            </FormElement>
          </>
        );
      }}
    </Field>
  );
}
